import React, { useState } from 'react';
import { Calendar, Users, MapPin, Ship } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { yachts, destinations } from '../data/mock';

const BookingPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    yacht: '',
    destination: '',
    startDate: '',
    endDate: '',
    guests: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock submission
    console.log('Booking submitted:', formData);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-950">
        <Navbar />
        <section className="pt-32 pb-24">
          <div className="max-w-2xl mx-auto px-4 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-orange-500/10 flex items-center justify-center">
              <Ship className="w-10 h-10 text-orange-500" />
            </div>
            <h1 className="text-3xl font-serif font-bold text-white mb-4">Booking Request Received!</h1>
            <p className="text-white/70 mb-8">
              Thank you for your interest in Azure Sovereign. Our team will contact you within 24 hours to discuss your luxury charter experience.
            </p>
            <a
              href="/"
              className="inline-block px-8 py-4 bg-orange-500 text-white font-medium rounded-lg hover:bg-orange-600 transition-colors"
            >
              Return Home
            </a>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950">
      <Navbar />
      
      {/* Header */}
      <section className="pt-32 pb-16 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-white mb-4">
            Book Your Charter
          </h1>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            Complete the form below and our team will create a bespoke yachting experience for you
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="py-16 bg-gray-950">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Info */}
            <div className="p-6 bg-gray-900 rounded-2xl border border-gray-800">
              <h2 className="text-xl font-semibold text-white mb-6">Personal Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-white/70 text-sm mb-2">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-orange-500 transition-colors"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-orange-500 transition-colors"
                    placeholder="john@example.com"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-white/70 text-sm mb-2">Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-orange-500 transition-colors"
                    placeholder="+1 234 567 8900"
                  />
                </div>
              </div>
            </div>

            {/* Charter Details */}
            <div className="p-6 bg-gray-900 rounded-2xl border border-gray-800">
              <h2 className="text-xl font-semibold text-white mb-6">Charter Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-white/70 text-sm mb-2">
                    <Ship className="w-4 h-4 inline mr-2" />
                    Preferred Yacht
                  </label>
                  <select
                    name="yacht"
                    value={formData.yacht}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-orange-500 transition-colors"
                  >
                    <option value="">Select a yacht</option>
                    {yachts.map((yacht) => (
                      <option key={yacht.id} value={yacht.id}>
                        {yacht.name} - {yacht.type}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">
                    <MapPin className="w-4 h-4 inline mr-2" />
                    Destination
                  </label>
                  <select
                    name="destination"
                    value={formData.destination}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-orange-500 transition-colors"
                  >
                    <option value="">Select a destination</option>
                    {destinations.map((dest) => (
                      <option key={dest.id} value={dest.id}>
                        {dest.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">
                    <Calendar className="w-4 h-4 inline mr-2" />
                    Start Date *
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-orange-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">
                    <Calendar className="w-4 h-4 inline mr-2" />
                    End Date *
                  </label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-orange-500 transition-colors"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-white/70 text-sm mb-2">
                    <Users className="w-4 h-4 inline mr-2" />
                    Number of Guests *
                  </label>
                  <input
                    type="number"
                    name="guests"
                    value={formData.guests}
                    onChange={handleChange}
                    required
                    min="1"
                    max="20"
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-orange-500 transition-colors"
                    placeholder="Number of guests"
                  />
                </div>
              </div>
            </div>

            {/* Additional Info */}
            <div className="p-6 bg-gray-900 rounded-2xl border border-gray-800">
              <h2 className="text-xl font-semibold text-white mb-6">Additional Information</h2>
              <div>
                <label className="block text-white/70 text-sm mb-2">Special Requests or Questions</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-orange-500 transition-colors resize-none"
                  placeholder="Tell us about any special requirements, dietary preferences, or occasions you're celebrating..."
                />
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              className="w-full py-4 bg-orange-500 text-white font-medium rounded-lg hover:bg-orange-600 transition-colors"
            >
              Submit Booking Request
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BookingPage;
